import {
  CustomIdp,
  MFAMethods,
  MFAPreference,
  SocialIdp,
  StoreContactType,
} from '@/config/constants';
import {
  IntegraAccount,
  IntegraProfile,
  SessionLogonRequest,
  isErrorResponse,
} from '@/api/integraLink/types';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import {
  SignInOutput,
  SignUpOutput,
  UpdateMFAPreferenceInput,
  confirmSignIn as awsConfirmSignIn,
  confirmSignUp as awsConfirmSignUp,
  deleteUser as awsDeleteUser,
  signIn as awsSignIn,
  signOut as awsSignOut,
  signUp as awsSignUp,
  confirmResetPassword,
  confirmUserAttribute,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  setUpTOTP,
  signInWithRedirect,
  updateMFAPreference,
  updatePassword,
  updateUserAttributes,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { awsConfig, getSsoUrl } from '@/config/aws';
import { postSessionLogon, postSignOut } from '@/api/integraLink/service';
import { startServices, stopServices } from '../serviceControl';

import { Amplify } from 'aws-amplify';
import { AuthStorage } from './store';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { ConsoleLogger } from 'aws-amplify/utils';
import axios from 'axios';
import capConfig from '../../../capacitor.config';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import config from '@/config';
import { displayMFASelectionAlert } from '../alertService';
import { errorDetails } from '../logger/errorDetails';
import { integraUserToContact } from '@/utils/helpers';
import logger from '../logger';
import { populateAppDetailsFromSignIn } from '../appDetails';
import router from '@/router';
import { showErrorToast } from '@/utils/toast';
import { t } from '@/utils/i18n';
import { useStore } from '@/store';

export type UserAttributes = {
  userId: string;
  name: string;
  email: string;
  mobile?: string;
  telephone?: string;
  mfaLastDate?: string;
  identities?: string;
};

export const setupAuth = async () => {
  try {
    ConsoleLogger.LOG_LEVEL =
      import.meta.env.MODE === 'development' ? 'DEBUG' : 'ERROR';
    Amplify.configure(config.services.aws.awsConfig);
    const authStorage = new AuthStorage();
    await authStorage.sync();
    cognitoUserPoolsTokenProvider.setKeyValueStorage(authStorage);
  } catch (error) {
    logger.error('setupAuth', errorDetails(error));
  }
};

export const isSignedIn = async (): Promise<boolean> => {
  try {
    const result = await getCurrentUser();
    logger.debug('isSignedIn', { result });

    return true;
  } catch (error: any) {
    // if they were logged in, we need to log them out if their session has been expired
    const isUserSignedIn = useStore().isSignedIntoIntegra;
    if (isUserSignedIn && error.name === 'UserUnAuthenticatedException') {
      await handleAutoSignOut();
    }

    if (error.name === 'UserUnAuthenticatedException') {
      logger.debug('isSignedIn', error.name);
    } else {
      logger.error('isSignedIn', errorDetails(error));
    }

    return false;
  }
};

export const getUsername = async (): Promise<string> => {
  try {
    const { username } = await getCurrentUser();
    return username;
  } catch (e: any) {
    logger.error('getUsername', e.message ?? e);
    throw e;
  }
};

export const isSso = async (): Promise<boolean> => {
  try {
    const { identities } = await getUserAttributes();

    logger.debug('isSso', { identities });

    return !!identities;
  } catch (e: any) {
    logger.error('isSso', e.message ?? e);
    return false;
  }
};

export const getJwt = async (
  forceRefresh = false,
): Promise<string | undefined> => {
  try {
    const { tokens } = await fetchAuthSession({ forceRefresh });
    if (!tokens) {
      logger.error('getJwt: No tokens');
      throw new Error('No id JWT');
    }

    const { idToken } = tokens;

    if (!idToken) {
      logger.error('getJwt: No id JWT');
      throw new Error('No id JWT');
    }

    return idToken.toString();
  } catch (err: any) {
    logger.error('getJwt', err.message ?? err);

    // if they were logged in, we need to log them out if their session has been expired
    const isUserSignedIn = useStore().isSignedIntoIntegra;
    if (isUserSignedIn && err.name === 'UserUnAuthenticatedException') {
      await handleAutoSignOut();
    }
  }
};

/**
 * The JWT for a request that cannot be made without one. Callers that would
 * otherwise send an empty `Bearer` header - which the API answers with a 401
 * indistinguishable from a genuinely rejected token - should use this instead.
 */
export const requireJwt = async (forceRefresh = false): Promise<string> => {
  const token = await getJwt(forceRefresh);

  if (!token) {
    throw new Error('No Cognito JWT available');
  }

  return token;
};

export const getUserAttributes = async (): Promise<UserAttributes> => {
  try {
    const attributes = await fetchUserAttributes();

    return {
      userId: attributes['custom:user_id'] || '',
      name: attributes['name'] || '',
      email: attributes['email'] || '',
      mobile: attributes['phone_number'] || '',
      telephone: attributes['custom:telephone'] || '',
      mfaLastDate: attributes['custom:mfa_last_date'] || '',
      identities: attributes['identities'] || '',
    };
  } catch (e: any) {
    logger.error('getUserAttributes', e.message ?? e);
    throw e;
  }
};

export const updateUserAttribute = async (
  name: string,
  value: string,
): Promise<void> => {
  const userAttributes: Record<string, string> = {};
  userAttributes[name] = value;

  try {
    const result = await updateUserAttributes({ userAttributes });
    logger.debug('updateUserAttribute', { result });
  } catch (error) {
    logger.error('updateUserAttribute', errorDetails(error));
  }
};

export const signUp = async (
  email: string,
  password: string,
  name: string,
  mobile?: string,
): Promise<SignUpOutput> => {
  try {
    const result = await awsSignUp({
      username: email,
      password,

      options: {
        userAttributes: {
          name,
          ...(mobile && {
            phone_number: mobile,
          }),
        },
        clientMetadata: { operatorId: config.app.companyDetails.ownerId },
      },
    });

    // Clear any other defaults
    useStore().signInDetails.defaults = { email };
    logger.info('signUp', { result });
    return result;
  } catch (e: any) {
    logger.error('signUp', e.message ?? e);
    throw e;
  }
};

export const confirmSignUp = async (
  username: string,
  confirmationCode: string,
): Promise<any> => {
  try {
    const result = await awsConfirmSignUp({
      username,
      confirmationCode,
      options: {
        clientMetadata: { operatorId: config.app.companyDetails.ownerId },
      },
    });
    logger.info('confirmSignUp', { result });
    return result;
  } catch (e: any) {
    logger.error('confirmSignUp', e.message ?? e);
    throw e;
  }
};

const resetMFA = () => {
  useStore().mfaDetails = {
    wrongPassCount: 0,
    pendingSMS: false,
    pendingTOTP: false,
    email: '',
    totpSetupDetails: undefined,
  };
};

const isSocialIdp = (p: CustomIdp | SocialIdp): p is SocialIdp =>
  Object.values(SocialIdp).includes(p as SocialIdp);

export const ssoSignIn = async (
  identityProvider: CustomIdp | SocialIdp,
): Promise<void> => {
  try {
    if (await isSignedIn()) {
      await signOut();
    }

    logger.info('ssoSignIn', { identityProvider });
    useStore().signInDetails.defaults = {};

    if (Capacitor.isNativePlatform()) {
      const url = getSsoUrl(identityProvider);
      Browser.open({ url });
    } else {
      const provider = isSocialIdp(identityProvider)
        ? (identityProvider as SocialIdp)
        : { custom: identityProvider as string };

      await signInWithRedirect({ provider });
    }
  } catch (e: any) {
    logger.error('ssoSignIn', e.message ?? e);
    throw e;
  }
};

export const signIn = async (
  email: string,
  password: string,
  remember: boolean,
): Promise<void> => {
  const store = useStore();

  try {
    store.resetStore(false); // we normally do this on SignOut, but that may have failed if we failed to SignOut fully

    if (store.mfaDetails.email !== email) {
      resetMFA();
    }

    const signInRes = await awsSignIn({
      username: email,
      password,
      options: {
        clientMetadata: {
          wrongPassCount: store.mfaDetails.wrongPassCount.toString(),
          operatorId: config.app.companyDetails.ownerId,
        },
      },
    });

    await handleSignIn(email, remember, signInRes);
  } catch (e: any) {
    // NotAuthorizedException: Incorrect username or password. (wrong password)
    if (e.name === 'NotAuthorizedException') {
      store.mfaDetails.email = email;
      store.incrementWrongPassCount();
    }

    logger.error('signIn', e.message ?? e);
    throw e;
  }
};

const handleSignIn = async (
  email: string,
  remember: boolean,
  signInRes: SignInOutput,
) => {
  const { signInDetails } = useStore();
  signInDetails.defaults = {};
  signInDetails.rememberedEmail = remember ? email : undefined;

  await handleSignInNextSteps(signInRes, email);
};

const handleSignInNextSteps = async (
  signInRes: SignInOutput,
  email?: string,
) => {
  const { nextStep } = signInRes;
  logger.info('signInStep', { signInStep: nextStep.signInStep });
  const store = useStore();
  switch (nextStep.signInStep) {
    case 'DONE':
      await continueSignIn();
      break;

    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
    case 'RESET_PASSWORD':
      if (
        nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        store.signInDetails.confirmSignInPending = true;
      }
      router.push({
        name: 'setPassword',
        params: {
          initialEmail: email,
        },
      });

      break;

    case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION': {
      logger.debug('MFA selection initiated', {
        allowedMFATypes: nextStep.allowedMFATypes,
      });
      const method = await displayMFASelectionAlert();
      if (!method) return;

      await handleMFASelection(method);
      break;
    }

    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
      store.mfaDetails.pendingSMS = true;
      break;

    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      store.mfaDetails.pendingTOTP = true;
      break;

    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
      store.mfaDetails.totpSetupDetails = nextStep.totpSetupDetails;
      router.push({ name: 'totpSetup', params: { email } });
      break;
    }

    default:
      throw new Error('Unknown step');
  }
};

export const confirmSignIn = async (challengeResponse: string) => {
  try {
    await awsConfirmSignIn({ challengeResponse });

    logger.info('signInConfirmed');
    await continueSignIn();
  } catch (e: any) {
    await awsSignOut();
    logger.error('confirmSignIn', e.message ?? e);
    throw e;
  }
};

export const continueSignIn = async () => {
  resetMFA();
  await handleIntegraLogOn();
};

// Identifies the session by reference only: the profile also carries API keys
// and the user's contact details, which stay out of the logs.
const summariseProfile = (profile: IntegraProfile, isGuest = false) => ({
  operatorId: config.app.companyDetails.ownerId,
  isGuest,
  systemName: profile.settings?.systemName,
  systemVersion: profile.settings?.version,
  host: profile.user?.host,
  loggedIn: profile.loggedIn,
  profileRef: profile.reference,
  contactRef: profile.user?.contactRef,
  accountRef: profile.user?.accountRef,
  accountCount: profile.accounts?.length,
  sessionExpiryUTC: profile.credentialSession?.expiryUTC,
});

const handleIntegraLogOn = async (): Promise<void> => {
  try {
    const { email } = await getUserAttributes();
    const logOnRes = await postSessionLogon({
      email,
    });

    if (isErrorResponse(logOnRes)) {
      throw new Error(logOnRes.error.text);
    }

    logger.info('handleIntegraLogOn', summariseProfile(logOnRes.profile));
    const store = useStore();
    const integraProfile = logOnRes.profile;
    if (integraProfile) {
      store.integraProfile = integraProfile;
      store.isGuestSession = false;
      if (integraProfile.loggedIn) {
        await completeSignIn(integraProfile);
      } else if (!(await autoSelectSingleProfile(integraProfile, email))) {
        await router.push({ name: 'selectProfile' });
      }
    }
  } catch (e: any) {
    logger.error('handleIntegraLogOn', e.message ?? e);
    throw e;
  }
};

export const handleGuestIntegraLogOn = async (): Promise<void> => {
  try {
    const logOnRes = await postSessionLogon({
      guest: true,
    });

    if (isErrorResponse(logOnRes)) {
      throw new Error(logOnRes.error.text);
    }

    logger.info(
      'handleGuestIntegraLogOn',
      summariseProfile(logOnRes.profile, true),
    );
    const store = useStore();
    store.integraProfile = logOnRes.profile;
    store.isGuestSession = true;
  } catch (e: any) {
    logger.error('handleGuestIntegraLogOn', e.message ?? e);
    throw e;
  }
};

export const completeSignIn = async (
  profile: IntegraProfile,
  accountSignUpRef?: string,
) => {
  await handleIntegraProfile(profile, accountSignUpRef);
  await populateAppDetailsFromSignIn();
  await startServices('signin');
  useStore().mfaDetails.wrongPassCount = 0;
  await router.push({ name: 'home' });
};

const handleIntegraProfile = async (
  profile: IntegraProfile,
  accountSignUpRef?: string,
) => {
  const store = useStore();
  let accounts: IntegraAccount[] = store.integraProfile.accounts;
  if (accountSignUpRef) {
    // Only update "profile" of account that is being signed up to
    const accountSignUp = profile.accounts.find(
      (a) => a.accountRef === accountSignUpRef,
    );

    if (accountSignUp) {
      const filteredAccounts = accounts.filter(
        (account) => account.accountRef !== accountSignUpRef,
      );

      accounts = [...filteredAccounts, accountSignUp];
    }
  }

  // Only update "profiles" on initial signIn call
  store.integraProfile = { ...profile, accounts };

  // Set profile and job contacts
  const contact = integraUserToContact(profile.user);
  await store.setContact(
    [StoreContactType.JOB, StoreContactType.PROFILE],
    contact,
  );
};

export const signOutCognito = async () => {
  try {
    await awsSignOut({ global: true });
  } catch (e: any) {
    logger.error('signOutCognito', e.message ?? e);
    throw e;
  } finally {
    useStore().resetStore();
  }
};

export const signOut = async (): Promise<void> => {
  try {
    if (!(await isSso()) || Capacitor.isNativePlatform()) {
      await router.replace({ name: 'signin' });
    }

    await stopServices();
    await signOutFromIntegra();
    await signOutCognito();
  } catch (e: any) {
    logger.error('signOut', e.message ?? e);
    throw e;
  }
};

export const signOutFromIntegra = async (): Promise<void> => {
  try {
    const signOutRes = await postSignOut();
    if (isErrorResponse(signOutRes)) {
      throw new Error(signOutRes.error.text);
    }
  } catch (e: any) {
    // Even if integra SignOut fails, we still want to log out of everything else
    logger.warn('signOutFromIntegra', e.message ?? e);
  }
};

let autoSigningOut = false;

/**
 * Tears the session down and returns the user to sign-in. Requests in flight
 * together fail together, so this is guarded against re-entry - otherwise a
 * screen with several outstanding requests tears down once per request and
 * stacks up a message for each.
 */
export const handleAutoSignOut = async (): Promise<void> => {
  const store = useStore();

  if (!store.isGuestSession) {
    if (autoSigningOut) {
      return;
    }

    autoSigningOut = true;
    // Warn rather than info: this ends the user's session, and it is the entry
    // the logs are searched for when someone reports being thrown out.
    logger.warn('handleAutoSignOut', {
      hasIntegraToken: !!store.integraLinkJwt,
      isSignedIntoIntegra: !!store.isSignedIntoIntegra,
    });
    try {
      // Raised before the tear-down begins: signing out an SSO user navigates
      // away to the identity provider's logout endpoint, and nothing after that
      // call runs.
      showErrorToast(t('toast.sessionExpired'));
      await stopServices();
      await awsSignOut();
      router.replace({ name: 'signin' });
    } catch (e: any) {
      logger.error('handleAutoSignOut', e.message ?? e);
      throw e;
    } finally {
      store.resetStore();
      autoSigningOut = false;
    }
  }
};

export const forgotPassword = async (username: string): Promise<any> => {
  try {
    const result = await resetPassword({
      username,
      options: {
        clientMetadata: { operatorId: config.app.companyDetails.ownerId },
      },
    });
    logger.info('forgotPasword', { result });
    return result;
  } catch (e: any) {
    logger.error('forgotPassword', e.message ?? e);
    throw e;
  }
};

export const confirmForgotPassword = async (
  username: string,
  confirmationCode: string,
  newPassword: string,
): Promise<void> => {
  try {
    await confirmResetPassword({
      username,
      confirmationCode,
      newPassword,
      options: {
        clientMetadata: { operatorId: config.app.companyDetails.ownerId },
      },
    });
    logger.info('forgotPasswordSubmit', true);
  } catch (e: any) {
    logger.error('confirmForgotPassword', e.message ?? e);
    throw e;
  }
};

export const deleteUser = async (): Promise<void> => {
  try {
    await stopServices();
    await awsDeleteUser();
    logger.info('deleteUser', true);
  } catch (e: any) {
    logger.error('deleteUser', e.message ?? e);
    throw e;
  } finally {
    useStore().resetStore();
  }
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string,
): Promise<any> => {
  try {
    const result = await updatePassword({ oldPassword, newPassword });
    logger.info('changePassword', { result });
    return result;
  } catch (e: any) {
    logger.error('changePassword', e.message ?? e);
    throw e;
  }
};

export type SingleAutoSelectProfile =
  | { type: 'signIn'; profile: IntegraAccount }
  | { type: 'signUp'; profile: IntegraAccount };

/**
 * Finds the one profile that should be auto-selected when a user has only a
 * single sign-in or sign-up option, so callers can skip showing a profile
 * picker. Returns undefined when there isn't exactly one candidate.
 */
export const getSingleAutoSelectProfile = (
  accounts?: IntegraAccount[] | null,
): SingleAutoSelectProfile | undefined => {
  const contactProfiles = accounts?.filter((acc) => acc.contactRef) ?? [];
  const signUpProfiles =
    accounts?.filter((acc) => !acc.contactRef && !acc.email) ?? [];

  if (contactProfiles.length + signUpProfiles.length !== 1) {
    return undefined;
  }

  const [contactProfile] = contactProfiles;
  const [signUpProfile] = signUpProfiles;

  if (contactProfile) {
    return { type: 'signIn', profile: contactProfile };
  }

  if (signUpProfile) {
    return { type: 'signUp', profile: signUpProfile };
  }

  return undefined;
};

/**
 * When there is exactly one profile to sign in/up with and no further user
 * input is required (e.g. no sign-up PIN to enter), log straight in rather
 * than navigating to the select-profile page just to immediately leave it.
 * Returns true if it handled sign in/up, false if the caller should fall
 * back to showing the select-profile page.
 */
const autoSelectSingleProfile = async (
  integraProfile: IntegraProfile,
  email?: string | null,
): Promise<boolean> => {
  const selection = getSingleAutoSelectProfile(integraProfile.accounts);

  if (selection?.type === 'signIn') {
    await integraLogOn({
      contactRef: selection.profile.contactRef ?? undefined,
      email: email ?? undefined,
    });
    return true;
  }

  if (selection?.type === 'signUp' && !selection.profile.signUp?.signUpByPin) {
    await integraLogOn(
      {
        accountRef: selection.profile.accountRef ?? undefined,
        email: email ?? undefined,
      },
      selection.profile.accountRef ?? undefined,
    );
    return true;
  }

  return false;
};

export const integraLogOn = async (
  body: SessionLogonRequest,
  accountSignUpRef?: string,
) => {
  const { name, mobile } = await getUserAttributes();
  body = { ...body, name, mobile };
  const logOnRes = await postSessionLogon(body);

  if (isErrorResponse(logOnRes)) {
    throw new Error(logOnRes.error.text);
  }

  logger.info('integraLogOn', summariseProfile(logOnRes.profile));
  await completeSignIn(logOnRes.profile, accountSignUpRef);
};

const buildCognitoKeys = (userPoolClientId: string, username: string) => {
  const preKey = `CognitoIdentityServiceProvider.${userPoolClientId}`;
  const userKey = `${preKey}.${username}`;
  return {
    lastAuthUser: `${preKey}.LastAuthUser`,
    idToken: `${userKey}.idToken`,
    accessToken: `${userKey}.accessToken`,
    refreshToken: `${userKey}.refreshToken`,
    clockDrift: `${userKey}.clockDrift`,
  };
};

export const getSessionFromCode = async (code: string): Promise<void> => {
  try {
    const cognitoConfig = awsConfig.Auth?.Cognito;
    logger.info('getSessionFromCode');

    if (!cognitoConfig) {
      throw new Error('Cognito configuration is missing.');
    }

    const { loginWith, userPoolClientId } = cognitoConfig;
    if (
      !loginWith?.oauth?.domain ||
      !userPoolClientId ||
      !loginWith?.oauth?.redirectSignIn?.[0]
    ) {
      throw new Error('Cognito OAuth configuration is incomplete.');
    }

    const tokenUrl = `https://${loginWith.oauth.domain}/oauth2/token`;
    const urlEncodedData = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: userPoolClientId,
      code,
      redirect_uri: loginWith.oauth.redirectSignIn[0],
    }).toString();

    const tokens = await getTokens(tokenUrl, urlEncodedData);

    if (!tokens.access_token) {
      throw new Error('Access token is missing.');
    }

    const { clockDrift, username } = getSessionDetails(
      tokens.access_token,
      tokens.id_token,
    );

    if (
      username &&
      tokens.id_token &&
      tokens.access_token &&
      tokens.refresh_token
    ) {
      const keys = buildCognitoKeys(userPoolClientId, username);
      const keyValueStorage =
        cognitoUserPoolsTokenProvider.authTokenStore.keyValueStorage;

      keyValueStorage?.setItem(keys.lastAuthUser, username);
      keyValueStorage?.setItem(keys.idToken, tokens.id_token);
      keyValueStorage?.setItem(keys.accessToken, tokens.access_token);
      keyValueStorage?.setItem(keys.refreshToken, tokens.refresh_token);
      keyValueStorage?.setItem(keys.clockDrift, clockDrift ?? '0');
      logger.info('getSessionFromCode values set');
    } else {
      throw new Error('Missing values for session.');
    }
  } catch (e: any) {
    logger.error('getSessionFromCode', e.message ?? e);
    throw e;
  }
};

const getTokens = async (url: string, urlEncodedData: string) => {
  const response = await axios.post(url, urlEncodedData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });

  return response.data;
};

const decodeToken = (token: string): JwtPayload => {
  return jwtDecode<JwtPayload>(token);
};

const getSessionDetails = (
  accessToken: string,
  idToken: string,
): { clockDrift?: string; username?: string } => {
  let clockDrift;
  const now = Math.floor(Date.now() / 1000);
  const decodedAccess = decodeToken(accessToken);
  const decodedId = decodeToken(idToken);

  const accessTokenIat = decodedAccess.iat;
  const idTokenIat = decodedId.iat;

  if (accessTokenIat && idTokenIat) {
    clockDrift = (now - Math.min(accessTokenIat, idTokenIat)).toString();
  }

  return { clockDrift, username: decodedAccess.sub };
};

export const refreshIntegraSession = async (
  email: string,
  contactRef: number,
  accountRef: string,
) => {
  try {
    logger.info('refreshIntegraSession');
    const logOnRes = await postSessionLogon({
      email,
      contactRef,
      accountRef,
    });

    if (isErrorResponse(logOnRes)) {
      throw new Error(logOnRes.error.text);
    }

    logger.info(
      'refreshIntegraSession done',
      summariseProfile(logOnRes.profile),
    );
    await handleIntegraProfile(logOnRes.profile, accountRef);
  } catch (e: any) {
    logger.error('refreshIntegraSession', e.message ?? e);
    throw e;
  }
};

export const handleTOTPSetup = async (
  email?: string,
): Promise<{
  secret: string;
  url: URL;
}> => {
  try {
    const totpSetupDetails =
      useStore().mfaDetails.totpSetupDetails ?? (await setUpTOTP());

    return {
      secret: totpSetupDetails.sharedSecret,
      url: totpSetupDetails.getSetupUri(
        capConfig?.appName ?? 'FreedomGoX',
        email,
      ),
    };
  } catch (e: any) {
    logger.error('handleTOTPSetup', e.message ?? e);
    throw e;
  }
};

export const handleTOTPVerification = async (
  code: string,
  isInitialSetup: boolean,
  updatePreference = true,
) => {
  try {
    isInitialSetup
      ? await confirmSignIn(code)
      : await verifyTOTPSetup({ code });

    if (updatePreference) {
      await handleUpdateMFAPreference(MFAMethods.TOTP);
    }
  } catch (e: any) {
    logger.error('handleTOTPVerification', e.message ?? e);
    throw e;
  }
};

export const handleSMSVerification = async (
  code: string,
  updatePreference = true,
) => {
  try {
    await confirmUserAttribute({
      userAttributeKey: 'phone_number',
      confirmationCode: code,
    });

    if (updatePreference) {
      await handleUpdateMFAPreference(MFAMethods.SMS);
    }
  } catch (e: any) {
    logger.error('handleSMSVerification', e.message ?? e);
    throw e;
  }
};

export const handleUpdateMFAPreference = async (
  preference: MFAMethods | null,
) => {
  try {
    const current = await fetchMFAPreference();

    const isSMSEnabled = current.enabled?.includes(MFAMethods.SMS);
    const isTOTPEnabled = current.enabled?.includes(MFAMethods.TOTP);

    let settings: UpdateMFAPreferenceInput = {};

    if (!preference) {
      // Disable everything explicitly
      settings = {
        sms: MFAPreference.NOT_PREFERRED,
        totp: MFAPreference.NOT_PREFERRED,
      };
    } else {
      settings = {
        sms: isSMSEnabled ? MFAPreference.ENABLED : MFAPreference.DISABLED,
        totp: isTOTPEnabled ? MFAPreference.ENABLED : MFAPreference.DISABLED,
      };

      // Override selected preference
      if (preference === MFAMethods.SMS) {
        settings.sms = MFAPreference.PREFERRED;
      }

      if (preference === MFAMethods.TOTP) {
        settings.totp = MFAPreference.PREFERRED;
      }
    }

    await updateMFAPreference(settings);
  } catch (e: any) {
    logger.error('handleUpdateMFAPreference', e.message ?? e);
    throw e;
  }
};

export const handleDisableMFAMethod = async (method: MFAMethods) => {
  try {
    await updateMFAPreference(
      method === MFAMethods.TOTP
        ? {
            totp: MFAPreference.DISABLED,
            sms: MFAPreference.PREFERRED,
          }
        : {
            totp: MFAPreference.PREFERRED,
            sms: MFAPreference.DISABLED,
          },
    );
  } catch (e: any) {
    logger.error('handleDisableTOTP', e.message ?? e);
    throw e;
  }
};

export const handleFetchMFAPreference = async () => {
  try {
    return await fetchMFAPreference();
  } catch (e: any) {
    logger.error('handleFetchMFAPreference', e.message ?? e);
    throw e;
  }
};

export const handleMFASelection = async (mfaType: MFAMethods) => {
  try {
    const output = await awsConfirmSignIn({
      challengeResponse: mfaType,
    });

    await handleSignInNextSteps(output);
  } catch (e: any) {
    logger.error('handleMFASelection', e.message ?? e);
    throw e;
  }
};
